import React, { useId, useState } from 'react';

import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
// Predefined color options - 32 colors organized by hue and brightness
const PREDEFINED_COLORS = [
    // Primary Colors
    '#0078d4', // Blue
    '#107c10', // Green
    '#d83b01', // Orange
    '#e81123', // Red
    
    // Secondary Colors
    '#5c2d91', // Purple
    '#00bcf2', // Light Blue
    '#ff8c00', // Dark Orange
    '#a4262c', // Dark Red
    
    // Accent Colors
    '#881798', // Magenta
    '#00b294', // Teal
    '#ffb900', // Yellow
    '#ff6b6b', // Coral
    
    // Extended Blues
    '#004e8c', // Dark Blue
    '#00a1f1', // Sky Blue
    '#0078d4', // Azure Blue
    '#106ebe', // Navy Blue
    
    // Extended Greens
    '#107c10', // Forest Green
    '#00b294', // Mint Green
    '#00a651', // Emerald Green
    '#2e7d32', // Dark Green
    
    // Extended Reds & Pinks
    '#e81123', // Bright Red
    '#d13438', // Crimson Red
    '#ff6b6b', // Coral Pink
    '#c2185b', // Deep Pink
    
    // Extended Purples & Violets
    '#5c2d91', // Royal Purple
    '#881798', // Magenta
    '#9c27b0', // Violet
    '#673ab7', // Deep Purple
    
    // Extended Oranges & Yellows
    '#d83b01', // Burnt Orange
    '#ff8c00', // Dark Orange
    '#ffb900', // Golden Yellow
    '#ff9800', // Orange
    
    // Neutral Grays
    '#797775', // Medium Gray
    '#605e5c', // Dark Gray
    '#323130', // Very Dark Gray
    '#000000', // Black

    // Weaker / pastel tints — softer, low-saturation fills (e.g. for room/space
    // shading where strong colors are too dominant)
    '#cfe4f7', // Pale Blue
    '#cdebcd', // Pale Green
    '#fbddc7', // Pale Orange
    '#f8d0d4', // Pale Red
    '#ddd0ec', // Pale Purple
    '#c9ecfb', // Pale Light Blue
    '#ffe6c2', // Pale Amber
    '#ecc9cc', // Pale Rose

    '#f3d2f0', // Pale Magenta
    '#c8eae3', // Pale Teal
    '#ffeebf', // Pale Yellow
    '#ffd6d6', // Pale Coral
    '#dde7f3', // Pale Azure
    '#d6ecdc', // Pale Mint
    '#e6d8f6', // Pale Lavender
    '#e6e6e4', // Pale Gray
];

interface ColorSelectorProps {
    selectedColor?: string;
    onColorChange?: (color: string) => void;
    color?: string; // For backward compatibility
    colorChanged?: (color: string) => void; // For backward compatibility
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    /**
     * In einer Tabellenzeile: ohne eigene Beschriftung und auf Zeilenhöhe.
     *
     * Dort steht die Beschriftung schon im Spaltenkopf, und ein 60 Pixel hohes Feld mit einem Wort
     * darüber bestimmt die Höhe **jeder** Zeile — auch derer, in denen nichts anderes mehr als ein
     * Eingabefeld steht.
     */
    compact?: boolean;
}

export const ColorSelector: React.FC<ColorSelectorProps> = ({
    selectedColor,
    onColorChange,
    color,
    colorChanged,
    label = 'Color',
    placeholder = 'Select a color...',
    disabled = false,
    compact = false
}) => {
    // Handle both new and old prop patterns
    const finalSelectedColor = selectedColor || color || '#0078d4';
    const finalOnColorChange = onColorChange || colorChanged;
    const [isCalloutOpen, setIsCalloutOpen] = useState(false);
    const triggerId = useId();

    const handleColorSelect = (color: string) => {
        if (finalOnColorChange) {
            finalOnColorChange(color);
        }
        setIsCalloutOpen(false);
    };

    return (
        <Popover open={isCalloutOpen} onOpenChange={setIsCalloutOpen}>
            <div className="flex flex-col items-center gap-1">
                {label && !compact && <Label htmlFor={triggerId}>{label}</Label>}
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        id={triggerId}
                        disabled={disabled}
                        aria-label={compact ? label || undefined : undefined}
                        title={compact ? label || undefined : undefined}
                        className={compact
                            ? "flex h-8 w-8 items-center justify-center rounded-md border border-border/60 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"
                            : "flex h-[60px] w-[60px] flex-col items-center justify-center rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"}
                    >
                        {finalSelectedColor ? (
                            <>
                                <span
                                    className={compact ? "block h-6 w-6 rounded-sm" : "block h-8 w-8"}
                                    style={{ backgroundColor: finalSelectedColor }}
                                />
                                {/* Der Ausgleich unter dem Feld gehört zur großen Form; in der
                                    Zeile wäre er genau der verschenkte Platz. */}
                                {!compact && <span className="block h-[14px]" />}
                            </>
                        ) : (
                            <span className={compact
                                ? "text-xs text-muted-foreground"
                                : "mt-1 max-w-[50px] truncate text-center text-xs text-muted-foreground"}
                            >
                                {compact ? '—' : placeholder}
                            </span>
                        )}
                    </button>
                </PopoverTrigger>
            </div>

            <PopoverContent align="center" className="w-auto max-w-[320px] p-3">
                <div className="grid max-h-[200px] grid-cols-8 gap-1 overflow-y-auto">
                    {PREDEFINED_COLORS.map((color) => (
                        <button
                            key={color}
                            type="button"
                            aria-label={color}
                            className="h-6 w-6 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorSelect(color)}
                        />
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
};